import {
	ReactElement,
	RefObject,
	UIEvent,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import { PagerBase } from "../Pager/PagerBase";
import { Icon } from "../Icon/Icon";
import { useResizeObserver } from "client/Hooks";
import styles from "./ImageGallery.module.less";
import { never } from "client_legacy/ndaw/errors";
import { Anchor } from "../Anchor/Anchor";

export type ImageGalleryProps = {
	thumbnailUrls: readonly string[];
	activeIndex: number;
	onChangeActiveIndex: (index: number) => void;
	trackingId: string;
};

export const ImageGallery = (props: ImageGalleryProps): ReactElement => {
	const {
		activeIndex,
		thumbnailUrls,
		onChangeActiveIndex,
		trackingId
	} = props;

	const [scrollPosition, setScrollPosition] = useState<number>(0);

	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const listRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);

	const { width: containerWidth } = useResizeObserver(containerRef);

	const { width: listWidth } = useResizeObserver(listRef);

	const { canScrollToLeft, canScrollToRight } = useMemo(
		(): { canScrollToLeft: boolean; canScrollToRight: boolean; } => {
			return {
				canScrollToLeft: scrollPosition > 0,
				canScrollToRight: (scrollPosition + containerWidth) < listWidth
			};
		},
		[
			containerWidth,
			listWidth,
			scrollPosition
		]
	);

	const hasScroll: boolean = canScrollToLeft || canScrollToRight;

	useLayoutEffect(
		(): (() => void) => {
			// Without timeout, it will not fully scroll to the element
			const handle: SetTimeoutHandle = setTimeout(
				(): void => {
					listRef.current
						?.children[activeIndex]
						?.scrollIntoView({ behavior: "smooth" });
				},
				0
			);

			return (): void => clearTimeout(handle);
		},
		[activeIndex]
	);

	const nextHandler = useCallback(
		(): void => {
			const { current: list } = listRef;
			const { current: container } = containerRef;
			if (!list || !container) {
				return;
			}

			performScroll(
				container,
				list,
				"right"
			);
		},
		[]
	);

	const backHandler = useCallback(
		(): void => {
			const { current: list } = listRef;
			const { current: container } = containerRef;
			if (!list || !container) {
				return;
			}

			performScroll(
				container,
				list,
				"left"
			);
		},
		[]
	);

	const scrollHandler = useCallback(
		(args: UIEvent): void => {
			const scrollPosition: number = args.currentTarget
				.scrollLeft;
			setScrollPosition(scrollPosition);
		},
		[]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{hasScroll && (
				<div className={styles.arrowContainer}>
					<PagerBase>
						<PagerBase.Button
							kind="arrow"
							onClick={backHandler}
							trackingId="previous"
							isDisabled={!canScrollToLeft}
						>
							<Icon value="chevron-left" />
						</PagerBase.Button>
					</PagerBase>
				</div>
			)}

			<div
				ref={containerRef}
				className={styles.container}
				onScroll={scrollHandler}
			>
				<ul
					ref={listRef}
					className={styles.list}
				>
					{thumbnailUrls.map((x, i): ReactElement => (
						<li
							key={i}
							className={styles.listItem}
						>
							<Anchor
								onClick={() => onChangeActiveIndex(i)}
							>
								<img
									className={classNames(
										styles.thumbnail,
										{ [styles.thumbnail_active]: activeIndex === i }
									)}
									src={x}
									draggable={false}
								/>
							</Anchor>
						</li>
					))}
				</ul>
			</div>

			{hasScroll && (
				<div
					className={styles.arrowContainer}
				>
					<PagerBase>
						<PagerBase.Button
							kind="arrow"
							onClick={nextHandler}
							trackingId="next"
							isDisabled={!canScrollToRight}
						>
							<Icon value="chevron-right" />
						</PagerBase.Button>
					</PagerBase>
				</div>
			)}
		</div>
	);
};
ImageGallery.displayName = "ImageGallery";

function performScroll(
	scrollContainer: HTMLElement,
	list: HTMLUListElement,
	direction: "left" | "right"
): void {
	const {
		left: containerStart,
		right: containerEnd
	} = scrollContainer.getBoundingClientRect();

	const children: Element[] = Array.from(list.children);

	let doesMatchCriteria: (element: Element) => boolean;
	let fallbackOffset: number;

	switch (direction) {
		case "left":
			children.reverse();

			doesMatchCriteria = (element) => {
				const { right: itemEnd } = element.getBoundingClientRect();
				return itemEnd < containerStart;
			};

			fallbackOffset = 0;
			break;
		case "right":
			doesMatchCriteria = (element) => {
				const { left: itemStart } = element.getBoundingClientRect();
				return itemStart > containerEnd;
			};

			fallbackOffset = scrollContainer.scrollWidth;
			break;
		default:
			never(direction);
	}

	const targetElement: Element | undefined = children
		.find(
			(x, i): boolean => {
				const isLast: boolean = i === children.length - 1;
				return !isLast && doesMatchCriteria(x);
			}
		);

	if (targetElement) {
		targetElement.scrollIntoView({ behavior: "smooth" });
	} else {
		scrollContainer.scrollTo({
			left: fallbackOffset,
			behavior: "smooth"
		});
	}
}
